import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MdShield,
  MdDescription,
  MdLocalShipping,
  MdAssignmentReturn,
} from "react-icons/md";
import styles from "./MobilePolicyView.module.css";

export type PolicyTab = "privacy" | "refund" | "terms" | "shipping";

export interface MobilePolicyViewProps {
  initialPolicy?: PolicyTab;
}

/**
 * 1. Privacy Policy Component
 */
export function PrivacyPolicyContent() {
  return (
    <article className={styles.policyDocument}>
      <header className={styles.policyHeader}>
        <h1 className={styles.policyTitle}>Privacy Policy</h1>
        <p className={styles.lastUpdated}>
          <strong>Last Updated:</strong> 5 November 2026
        </p>
      </header>

      <p className={styles.leadParagraph}>
        At Mechmiles, we respect your privacy and understand the importance of
        keeping your personal information safe. This Privacy Policy explains how{" "}
        <strong>Junction of Tyres Private Limited (Mechmiles)</strong> collects,
        uses and protects your information when you visit our website, use our
        services or make a purchase.
      </p>

      <section className={styles.policySection}>
        <h2>1. Information We Collect</h2>
        <p>
          To provide a smooth and convenient experience, we may collect the
          following information:
        </p>
        <ul>
          <li>
            <strong>Personal Details:</strong> Name, mobile number, email address
            and residential or delivery address.
          </li>
          <li>
            <strong>Vehicle Details:</strong> Vehicle type, model, registration
            number, tyre size and other relevant specifications.
          </li>
          <li>
            <strong>Transaction Details:</strong> Order history, service
            bookings, payment status and billing information.
          </li>
          <li>
            <strong>Location Details:</strong> Location information, where
            permission is provided, to help identify nearby service centres.
          </li>
          <li>
            <strong>Technical Information:</strong> IP address, browser details,
            device information, cookies and website usage data.
          </li>
        </ul>
      </section>

      <section className={styles.policySection}>
        <h2>2. How We Use Your Information</h2>
        <p>We use your information to:</p>
        <ul>
          <li>Process orders and manage service bookings.</li>
          <li>Recommend suitable tyres, batteries and automotive products.</li>
          <li>Coordinate with service centres and delivery partners.</li>
          <li>
            Share order confirmations, service updates and payment
            notifications.
          </li>
          <li>Respond to customer enquiries and resolve complaints.</li>
          <li>Improve our website, products and customer experience.</li>
          <li>Share offers and promotional updates, where permitted.</li>
        </ul>
      </section>

      <section className={styles.policySection}>
        <h2>3. Sharing of Information</h2>
        <p>We do not sell your personal information to third parties.</p>
        <p>
          However, we may share relevant information with trusted service
          partners, payment gateways, delivery providers and technology service
          providers whenever required to complete your orders or provide our
          services.
        </p>
        <p>
          Information may also be shared with government or legal authorities
          when required by applicable law.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>4. Cookies</h2>
        <p>
          Our website uses cookies and similar technologies to improve browsing,
          remember preferences and understand how visitors use our platform.
        </p>
        <p>
          You can manage or disable cookies through your browser settings.
          Certain website features may not work properly if essential cookies
          are disabled. Where required, we will seek your consent for
          non-essential cookies.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>5. Information Security</h2>
        <p>
          We take reasonable measures to protect your personal information
          against unauthorised access, misuse, loss or disclosure. Access to
          customer information is restricted to authorised personnel and service
          providers who need it for business purposes.
        </p>
        <p>
          While we make every effort to maintain security, no online platform
          can guarantee complete protection against all risks.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>6. Data Retention</h2>
        <p>
          We retain your information only for as long as necessary to provide
          our services, maintain transaction records, meet legal requirements
          and resolve customer concerns. Once the information is no longer
          required, we take appropriate steps to delete or anonymise it,
          subject to applicable law.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>7. Your Privacy Rights</h2>
        <p>
          You can contact us to request access to, correction of or deletion of
          your personal information, subject to applicable legal requirements.
          You may also withdraw your consent or opt out of promotional
          communications at any time.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>8. Third-Party Websites</h2>
        <p>
          Our website may contain links to third-party websites or payment
          platforms. Mechmiles is not responsible for their privacy practices.
          We recommend reviewing their respective privacy policies before
          sharing any personal information.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>9. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes
          in our services, business practices or applicable laws. Any updates
          will be published on this page along with the revised date.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>10. Contact Us</h2>
        <p>
          If you have any questions, concerns or requests regarding your
          personal information, please get in touch with us.
        </p>
        <div className={styles.contactCard}>
          <p>
            <strong>Junction of Tyres Private Limited (Mechmiles)</strong>
          </p>
          <p>
            <strong>Website:</strong>{" "}
            <a
              href="https://www.mechmiles.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.mechmiles.com/
            </a>
          </p>
          <p>
            <strong>Email:</strong>{" "}
            <a href="mailto:info@mechmiles.com">info@mechmiles.com</a>
          </p>
          <p>
            <strong>Contact:</strong>{" "}
            <a href="tel:+919920034874">+91 9920034874</a>
          </p>
          <p>
            <strong>Address:</strong> 101, 2nd Avenue, Atul Projects, Subhash
            Nagar, Andheri (East), Mumbai – 400093, Maharashtra, India.
          </p>
        </div>

        <p>
          We value your trust and remain committed to handling your personal
          information with care and responsibility.
        </p>

        <div className={styles.taglineNote}>
          Mechmiles – Making Every Mile Better.
        </div>
      </section>
    </article>
  );
}

/**
 * 2. Return & Refund Policy Component
 */
export function ReturnAndRefundPolicy() {
  return (
    <article className={styles.policyDocument}>
      <header className={styles.policyHeader}>
        <h1 className={styles.policyTitle}>Return &amp; Refund Policy</h1>
        <p className={styles.lastUpdated}>
          <strong>Last Updated:</strong> 5 November 2026
        </p>
      </header>

      <p className={styles.leadParagraph}>
        At Mechmiles, we want every customer to have a smooth and satisfactory
        shopping experience. If you are not satisfied with your purchase or
        receive a damaged, defective or incorrect product, you can request a
        return or refund under the following conditions.
      </p>

      <section className={styles.policySection}>
        <h2>1. Return Eligibility</h2>
        <p>
          Products purchased through Mechmiles, including tyres and batteries,
          are eligible for return within{" "}
          <strong>30 days from the date of delivery</strong>, subject to the
          following conditions:
        </p>
        <ul>
          <li>Products must be unused and undamaged.</li>
          <li>
            Original packaging, accessories and other included items must be
            intact.
          </li>
          <li>Proof of purchase or order details must be provided.</li>
        </ul>

        <p>
          <strong>Non-returnable items include:</strong>
        </p>
        <ul>
          <li>Products that have been installed or used.</li>
          <li>Products damaged due to misuse or accidents.</li>
          <li>Specially ordered or customised products.</li>
          <li>Products purchased under promotional or final-sale offers.</li>
        </ul>
      </section>

      <section className={styles.policySection}>
        <h2>2. How to Request a Return</h2>
        <p>To initiate a return:</p>
        <ol>
          <li>
            Contact our customer support team with your order number and reason
            for return.
          </li>
          <li>
            Our team will review your request and share the return instructions.
          </li>
          <li>
            Pack the product securely in its original packaging, along with all
            included accessories.
          </li>
          <li>Return the product as instructed by our support team.</li>
        </ol>
        <p>All return requests are subject to product inspection and approval.</p>
      </section>

      <section className={styles.policySection}>
        <h2>3. Refund Process</h2>
        <p>
          Once the returned product is received and inspected, eligible refunds
          will be processed within <strong>7–10 business days</strong>.
        </p>
        <ul>
          <li>Refunds will be credited to the original payment method.</li>
          <li>Applicable product charges and taxes will be refunded.</li>
          <li>
            Shipping charges, if any, are non-refundable unless the return is
            due to an error on our part or a defective product.
          </li>
        </ul>
        <p>
          The actual time taken for the refund to reflect in your account may
          depend on your bank or payment provider.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>4. Damaged, Defective or Incorrect Products</h2>
        <p>
          If you receive a damaged, defective or incorrect product, please inform
          us within <strong>7 days of delivery</strong>.
        </p>
        <p>
          Our team will review the issue and guide you through the return or
          replacement process. Where the issue is confirmed to be our
          responsibility, the applicable return shipping costs will be borne by
          Mechmiles.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>5. Exchange Policy</h2>
        <p>
          We currently do not offer direct product exchanges. If you wish to
          purchase a different product, you may return the eligible item and
          place a fresh order, subject to our return policy.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>6. Service Cancellation and Refunds</h2>
        <p>
          Customers can request cancellation of a service booking before the
          service begins without a cancellation charge.
        </p>
        <p>
          Once the service has been completed or work has commenced with the
          customer's approval, refunds will be considered based on the nature of
          the concern and applicable service terms.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>7. Contact Us</h2>
        <p>
          For return, refund or product-related assistance, please contact our
          team:
        </p>
        <div className={styles.contactCard}>
          <p>
            <strong>Junction of Tyres Private Limited (Mechmiles)</strong>
          </p>
          <p>
            <strong>Email:</strong>{" "}
            <a href="mailto:info@mechmiles.com">info@mechmiles.com</a>
          </p>
          <p>
            <strong>Phone:</strong>{" "}
            <a href="tel:+919920034874">+91 99200 34874</a>
          </p>
          <p>
            <strong>Website:</strong>{" "}
            <a
              href="https://www.mechmiles.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.mechmiles.com/
            </a>
          </p>
          <p>
            <strong>Address:</strong> 101, 2nd Avenue, Atul Projects, Subhash
            Nagar, Andheri (East), Mumbai – 400093, Maharashtra, India.
          </p>
        </div>
      </section>

      <section className={styles.policySection}>
        <h2>8. Policy Updates</h2>
        <p>
          Mechmiles reserves the right to update this policy whenever necessary.
          Any changes will be published on this page with the revised date.
        </p>
        <p className={styles.thankYouNote}>
          Thank you for choosing Mechmiles. We appreciate your trust and look
          forward to serving you.
        </p>
      </section>
    </article>
  );
}

/**
 * 3. Terms & Conditions Component
 */
export function TermsAndConditionsPolicy() {
  return (
    <article className={styles.policyDocument}>
      <header className={styles.policyHeader}>
        <h1 className={styles.policyTitle}>Terms &amp; Conditions</h1>
        <p className={styles.lastUpdated}>
          <strong>Last Updated:</strong> 5 November 2026
        </p>
      </header>

      <p className={styles.leadParagraph}>
        Welcome to Mechmiles! These Terms &amp; Conditions govern your use of
        our website, mobile application, products and services. By accessing or
        using our platform, you agree to the terms mentioned below.
      </p>

      <p>
        Mechmiles is operated by{" "}
        <strong>Junction of Tyres Private Limited</strong> (&quot;Mechmiles&quot;,
        &quot;we&quot;, &quot;our&quot; or &quot;us&quot;).
      </p>

      <section className={styles.policySection}>
        <h2>1. Use of Our Services</h2>
        <p>
          Mechmiles provides automotive products and services, including tyres,
          batteries, accessories, puncture repair and vehicle maintenance.
          Customers are expected to provide accurate information and use our
          platform for lawful purposes.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>2. Products and Pricing</h2>
        <p>
          We make every effort to ensure that product details, specifications
          and prices displayed on our website are accurate. However, product
          availability and prices may change depending on stock, location and
          market conditions. Customers are advised to verify product
          compatibility before placing an order.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>3. Service Bookings</h2>
        <p>
          All service bookings are subject to appointment and service-centre
          availability. Estimated service times and costs may vary depending on
          the vehicle and work required. Any additional work or charges will be
          communicated to the customer for approval.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>4. Payments</h2>
        <p>
          Payments can be made through the options available on our website or
          application. Customers are responsible for providing accurate payment
          details. Applicable taxes and additional charges will be communicated
          during the transaction.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>5. Cancellation and Refunds</h2>
        <p>
          Customers may request cancellation of eligible service bookings
          through our support team. Refunds and product returns will be
          processed according to our Return &amp; Refund Policy. Completed
          services and used products may not qualify for cancellation or
          refunds, subject to applicable consumer rights.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>6. Product Warranty and Returns</h2>
        <p>
          Products purchased through Mechmiles are subject to the respective
          manufacturer&apos;s warranty and our Return &amp; Refund Policy.
          Customers should report defective, damaged or incorrect products
          within the prescribed reporting period.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>7. Service Partners</h2>
        <p>
          Mechmiles works with its own service locations and independent service
          partners. Service availability and execution may vary by location. We
          make reasonable efforts to maintain service quality and customer
          satisfaction.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>8. Shipping and Delivery</h2>
        <p>
          Product collection and fulfilment arrangements will be communicated to
          customers at the time of purchase. Please refer to our Shipping Policy
          for further details.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>9. Customer Responsibilities</h2>
        <p>
          Customers must provide correct personal and vehicle information,
          follow booking instructions, approve additional service costs and make
          payments as agreed.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>10. Intellectual Property</h2>
        <p>
          All website content, including logos, images, designs, text and
          branding, belongs to Junction of Tyres Private Limited or its
          respective licensors. Reproduction or commercial use without prior
          permission is prohibited.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>11. Privacy and Data Protection</h2>
        <p>
          Customer information is handled in accordance with our Privacy Policy
          and applicable Indian data protection laws. We use relevant information
          to process orders, manage bookings and improve our services.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>12. Limitation of Liability</h2>
        <p>
          Mechmiles makes reasonable efforts to provide reliable products and
          services. However, we cannot guarantee uninterrupted website access or
          continuous product availability. Nothing in these terms excludes any
          liability or consumer rights that cannot legally be excluded.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>13. Changes to Terms</h2>
        <p>
          Mechmiles reserves the right to update these Terms &amp; Conditions
          when required. Any changes will be published on this page with the
          revised date.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>14. Governing Law</h2>
        <p>
          These Terms &amp; Conditions shall be governed by the laws of India.
          Disputes shall be subject to the jurisdiction of competent courts in
          Mumbai, Maharashtra, subject to applicable law.
        </p>
      </section>

      <section className={styles.policySection}>
        <h2>15. Contact Us</h2>
        <p>For questions or assistance, please contact us:</p>
        <div className={styles.contactCard}>
          <p>
            <strong>Junction of Tyres Private Limited (Mechmiles)</strong>
          </p>
          <p>
            <strong>Website:</strong>{" "}
            <a
              href="https://www.mechmiles.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.mechmiles.com/
            </a>
          </p>
          <p>
            <strong>Email:</strong>{" "}
            <a href="mailto:info@mechmiles.com">info@mechmiles.com</a>
          </p>
          <p>
            <strong>Phone:</strong>{" "}
            <a href="tel:+919920034874">+91 9920034874</a>
          </p>
          <p>
            <strong>Address:</strong> 101, 2nd Avenue, Atul Projects, Subhash
            Nagar, Andheri (East), Mumbai – 400093, Maharashtra, India.
          </p>
        </div>

        <div className={styles.taglineNote}>
          Mechmiles – Making Every Mile Better.
        </div>
      </section>
    </article>
  );
}

/**
 * 4. Shipping And Delivery Policy Component
 */
export function ShippingAndDeliveryPolicy() {
  return (
    <article className={styles.policyDocument}>
      <header className={styles.policyHeader}>
        <h1 className={styles.policyTitle}>Shipping &amp; Delivery Policy</h1>
        <p className={styles.lastUpdated}>
          <strong>Last Updated:</strong> 5 November 2026
        </p>
      </header>

      <section className={styles.policySection}>
        <p className={styles.leadParagraph}>
          Thank you for choosing MechMiles for your purchases. We appreciate your
          support of our products/services. Please be aware that MechMiles does
          not offer shipping or delivery services.
        </p>
      </section>

      <div className={styles.contactCard}>
        <p>
          <strong>Junction of Tyres Private Limited (Mechmiles)</strong>
        </p>
        <p>
          <strong>Website:</strong>{" "}
          <a
            href="https://www.mechmiles.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.mechmiles.com/
          </a>
        </p>
        <p>
          <strong>Email:</strong>{" "}
          <a href="mailto:info@mechmiles.com">info@mechmiles.com</a>
        </p>
        <p>
          <strong>Phone:</strong>{" "}
          <a href="tel:+919920034874">+91 9920034874</a>
        </p>
      </div>

      <div className={styles.taglineNote}>
        Mechmiles – Making Every Mile Better.
      </div>
    </article>
  );
}

/**
 * MobilePolicyView - Main Page (No Header / No Footer)
 * Directly accessible via /app-policy, /app-policies, /mobile-policy, etc.
 */
export default function MobilePolicyView({
  initialPolicy,
}: MobilePolicyViewProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ policyType?: string }>();
  const basePath = process.env.REACT_APP_BASE_PATH || "/";

  const getTabFromLocation = (): PolicyTab => {
    // 1. From route param
    const routeParam = params.policyType?.toLowerCase();
    if (routeParam) {
      if (routeParam.includes("refund") || routeParam.includes("return"))
        return "refund";
      if (routeParam.includes("terms")) return "terms";
      if (routeParam.includes("shipping") || routeParam.includes("delivery"))
        return "shipping";
      if (routeParam.includes("privacy")) return "privacy";
    }

    // 2. From query param (?tab=... or ?policy=...)
    const searchParams = new URLSearchParams(location.search);
    const queryTab = (searchParams.get("tab") ||
      searchParams.get("policy"))?.toLowerCase();
    if (queryTab) {
      if (queryTab.includes("refund") || queryTab.includes("return"))
        return "refund";
      if (queryTab.includes("terms")) return "terms";
      if (queryTab.includes("shipping") || queryTab.includes("delivery"))
        return "shipping";
      if (queryTab.includes("privacy")) return "privacy";
    }

    // 3. From pathname
    const path = location.pathname.toLowerCase();
    if (path.includes("refund") || path.includes("return")) return "refund";
    if (path.includes("terms")) return "terms";
    if (path.includes("shipping") || path.includes("delivery"))
      return "shipping";
    if (path.includes("privacy")) return "privacy";

    return initialPolicy || "privacy";
  };

  const [activeTab, setActiveTab] = useState<PolicyTab>(getTabFromLocation);

  useEffect(() => {
    setActiveTab(getTabFromLocation());
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname, location.search, params.policyType, initialPolicy]);

  const handleTabClick = (tab: PolicyTab) => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Clean URL navigation without reloading
    const search = new URLSearchParams(location.search);
    search.set("tab", tab);
    navigate(`${location.pathname}?${search.toString()}`, { replace: true });
  };

  return (
    <div className={styles.pageContainer}>
      {/* Sticky Header with Policy Tabs */}
      <header className={styles.appHeader}>
        <div className={styles.brandRow}>
          <span className={styles.brandBadge}>
            <MdShield /> MechMiles
          </span>
          <span className={styles.appLabel}>Legal &amp; Policies</span>
        </div>

        <nav className={styles.tabsContainer} aria-label="Policy tabs">
            <button
            type="button"
            className={`${styles.tabBtn} ${
              activeTab === "terms" ? styles.tabBtnActive : ""
            }`}
            onClick={() => handleTabClick("terms")}
          >
            <MdDescription size={15} /> Terms &amp; Conditions
          </button>
          <button
            type="button"
            className={`${styles.tabBtn} ${
              activeTab === "privacy" ? styles.tabBtnActive : ""
            }`}
            onClick={() => handleTabClick("privacy")}
          >
            <MdShield size={15} /> Privacy Policy
          </button>

          <button
            type="button"
            className={`${styles.tabBtn} ${
              activeTab === "refund" ? styles.tabBtnActive : ""
            }`}
            onClick={() => handleTabClick("refund")}
          >
            <MdAssignmentReturn size={15} /> Return &amp; Refund
          </button>

        

          <button
            type="button"
            className={`${styles.tabBtn} ${
              activeTab === "shipping" ? styles.tabBtnActive : ""
            }`}
            onClick={() => handleTabClick("shipping")}
          >
            <MdLocalShipping size={15} /> Shipping &amp; Delivery
          </button>
        </nav>
      </header>

      {/* Main Content Area */}
      <main className={styles.contentCard}>
        {activeTab === "terms" && <TermsAndConditionsPolicy />}
        {activeTab === "privacy" && <PrivacyPolicyContent />}
        {activeTab === "refund" && <ReturnAndRefundPolicy />}
        {activeTab === "shipping" && <ShippingAndDeliveryPolicy />}
      </main>

      <footer className={styles.appFooter}>
        <p>
          © {new Date().getFullYear()} Junction of Tyres Pvt. Ltd. (MechMiles) •
          All rights reserved.
        </p>
      </footer>
    </div>
  );
}
